import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useI18n } from '@/i18n/i18n-provider';
import { filesApi } from '@/services/files.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Spinner } from '@/components/ui';
import { fileTypeCategory, isPreviewable } from './file-type';

interface PreviewDialogProps {
  readonly fileId: string;
  readonly fileName: string;
  readonly mimeType: string;
  readonly onClose: () => void;
}

/**
 * Renders a file inline when the browser can (`<img>`/`<iframe>`/
 * `<video>`/`<audio>` from the same signed URL `Download` already uses) —
 * otherwise a plain fallback with a link to open it in a new tab, rather
 * than pretending every one of the 15 allowed MIME types has a renderer.
 */
export function PreviewDialog({ fileId, fileName, mimeType, onClose }: PreviewDialogProps) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const download = useQuery({
    queryKey: ['cloud', 'preview-url', fileId],
    queryFn: () => filesApi.getDownloadUrl(fileId),
  });

  const category = fileTypeCategory(mimeType);
  const previewable = isPreviewable(mimeType);

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog dialog-lg"
      aria-labelledby="preview-title"
    >
      <div className="dialog-body">
        <h2 id="preview-title">{fileName}</h2>

        {download.isPending && <Spinner label={t('common.states.loading')} />}

        {download.isError && (
          <Alert tone="danger">
            {download.error instanceof ApiError
              ? t(`errors.${download.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
              : t('errors.INTERNAL_ERROR')}
          </Alert>
        )}

        {download.data &&
          (previewable ? (
            <div className="preview-frame">
              {category === 'IMAGE' && <img src={download.data.url} alt={fileName} />}
              {category === 'PDF' && <iframe src={download.data.url} title={fileName} />}
              {category === 'VIDEO' && <video src={download.data.url} controls />}
              {category === 'AUDIO' && <audio src={download.data.url} controls />}
            </div>
          ) : (
            <>
              <EmptyState title={t('files.preview.unavailable')} />
              <a
                href={download.data.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn"
                data-variant="secondary"
              >
                {t('files.preview.openInNewTab')}
              </a>
            </>
          ))}

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.close')}
          </Button>
        </footer>
      </div>
    </dialog>
  );
}
